import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import type { HiringHeroProgressbarProps } from './HiringHeroProgressbar.types';

const ANIMATION_DURATION = 2000;
const TROPHY_SHOW_DURATION = ANIMATION_DURATION + 500;

export const HiringHeroProgressbar = (props: HiringHeroProgressbarProps) => {
  const { value: providedValue, max, className } = props;
  const [showTrophy, setShowTrophy] = useState(false);
  const initRef = useRef(false);

  const value = Math.max(0, Math.min(providedValue, max));
  const ratio = value / max;
  const progressWidth = ratio * 100;

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
    } else {
      setShowTrophy(true);
      const timeout = setTimeout(() => setShowTrophy(false), TROPHY_SHOW_DURATION);

      return () => clearTimeout(timeout);
    }
  }, [ratio]);

  return (
    <div
      data-testid="hiring-hero-progress-bar"
      className={clsx('h-1.5 bg-neutral rounded-full', className)}
    >
      <div
        className={clsx('h-full rounded-full transition-[width] ease-in-out relative', {
          'bg-tertiary-green-600': progressWidth === 100,
          'bg-primary-pure-blue': progressWidth < 100 && progressWidth >= 50,
          'bg-primary-poppy-red': progressWidth < 50,
        })}
        style={{ width: `${progressWidth}%`, transitionDuration: `${ANIMATION_DURATION}ms` }}
        data-testid="hiring-hero-progress"
      >
        {showTrophy && (
          <span className="absolute text-xl -translate-x-1/2 -translate-y-1/2 left-full top-1/2">
            🏆
          </span>
        )}
      </div>
    </div>
  );
};

HiringHeroProgressbar.displayName = 'HiringHeroProgressbar';
